import { useState } from 'react';
import { ExternalLink, Dna, Activity } from 'lucide-react';

function Meter({ value, max = 1, color, label, sublabel }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="font-bold tabular-nums" style={{ color }}>{typeof value === 'number' ? value.toFixed(4) : value}</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
      {sublabel && <div className="text-xs text-gray-400 mt-1">{sublabel}</div>}
    </div>
  );
}

const TAB_LABELS = ['Overview', 'Constraint', 'Disease', 'Expression'];

function OverviewTab({ gene }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="flex flex-col gap-3">
        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">Full Name</div>
          <div className="font-semibold text-gray-900">{gene.full_name}</div>
        </div>

        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 flex flex-col gap-2">
          <div className="text-xs text-gray-500 mb-1">Inheritance Pattern</div>
          {gene.inheritance.map((inh) => (
            <span key={inh} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold w-fit"
              style={{ background: '#eff6ff', color: '#1d4ed8' }}>
              <Dna size={14} />
              {inh === 'AD' ? 'Autosomal Dominant' : inh === 'AR' ? 'Autosomal Recessive' : inh}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {[
          { label: 'OMIM', id: gene.omim, href: `https://omim.org/entry/${gene.omim}`, desc: gene.omim_phenotype },
          { label: 'Orphanet', id: gene.orphanet, href: `https://www.orpha.net/consor/cgi-bin/OC_Exp.php?lng=EN&Expert=${gene.orphanet.replace('ORPHA', '')}`, desc: gene.orphanet_disease },
          { label: 'ClinGen', id: gene.clingen_gene_id, href: `https://clinicalgenome.org/`, desc: 'Gene-Disease Validity' },
        ].map(({ label, id, href, desc }) => (
          <a key={label} href={href} target="_blank" rel="noopener noreferrer"
            className="p-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all flex items-center justify-between group">
            <div>
              <div className="text-xs text-gray-500 mb-0.5">{label}</div>
              <div className="font-mono text-sm font-semibold text-gray-800">{id}</div>
              <div className="text-xs text-gray-500 mt-0.5 leading-snug">{desc}</div>
            </div>
            <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-500 shrink-0 ml-2" />
          </a>
        ))}
      </div>
    </div>
  );
}

function ConstraintTab({ gene }) {
  const pLIColor = gene.gnomad_pLI >= 0.9 ? '#ef4444' : gene.gnomad_pLI >= 0.5 ? '#f59e0b' : '#22c55e';
  const loeufColor = gene.gnomad_loeuf <= 0.35 ? '#ef4444' : gene.gnomad_loeuf <= 0.7 ? '#f59e0b' : '#22c55e';
  const zColor = gene.gnomad_z_missense >= 3 ? '#ef4444' : gene.gnomad_z_missense >= 1 ? '#f59e0b' : '#22c55e';

  return (
    <div className="flex flex-col gap-5">
      <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-sm text-red-800">
        <strong>Highly constrained gene</strong> — BRCA1 shows strong intolerance to loss-of-function variants in the general population.
      </div>
      <Meter value={gene.gnomad_pLI} max={1} color={pLIColor} label="pLI Score (gnomAD)"
        sublabel={`Probability of LoF intolerance. Values ≥0.9 indicate high constraint. Current: ${gene.gnomad_pLI}`} />
      <Meter value={gene.gnomad_loeuf} max={2} color={loeufColor} label="LOEUF (gnomAD)"
        sublabel={`Loss-of-function observed/expected upper bound. Lower = more constrained. Current: ${gene.gnomad_loeuf}`} />
      <Meter value={gene.gnomad_z_missense} max={5} color={zColor} label="Z-score Missense (gnomAD)"
        sublabel={`Missense constraint z-score. Higher = more constrained. Current: ${gene.gnomad_z_missense}`} />
    </div>
  );
}

function DiseaseTab({ gene }) {
  const hiColor = gene.haploinsufficiency.color === 'red' ? { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' }
    : { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' };
  const tsColor = gene.triplosensitivity.color === 'red' ? { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' }
    : { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' };

  return (
    <div className="flex flex-col gap-4">
      {[
        { title: 'Haploinsufficiency', data: gene.haploinsufficiency, c: hiColor },
        { title: 'Triplosensitivity', data: gene.triplosensitivity, c: tsColor },
      ].map(({ title, data, c }) => (
        <div key={title} className="p-4 rounded-xl border" style={{ background: c.bg, borderColor: c.border }}>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-gray-800">{title}</span>
            <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: c.border, color: c.text }}>
              Score {data.score}
            </span>
          </div>
          <p className="text-sm mt-2" style={{ color: c.text }}>{data.label}</p>
        </div>
      ))}

      <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
        <div className="text-xs text-gray-500 font-medium mb-2">Function</div>
        <p className="text-sm text-gray-700 leading-relaxed">{gene.function}</p>
      </div>
    </div>
  );
}

function ExpressionTab({ gene }) {
  const expressionColors = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6', '#10b981'];
  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-gray-600 leading-relaxed">
        <strong>{gene.symbol}</strong> shows highest expression in the following tissues:
      </div>
      <div className="flex flex-wrap gap-2">
        {gene.expression.map((tissue, i) => (
          <div key={tissue} className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium"
            style={{ background: `${expressionColors[i % expressionColors.length]}15`, borderColor: `${expressionColors[i % expressionColors.length]}30`, color: expressionColors[i % expressionColors.length] }}>
            <Activity size={14} />
            {tissue.charAt(0).toUpperCase() + tissue.slice(1)}
          </div>
        ))}
      </div>
      <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 text-sm text-blue-800 leading-relaxed">
        Expression data sourced from GTEx and Human Protein Atlas. BRCA1 is broadly expressed with enrichment in tissues with high proliferative activity.
      </div>
    </div>
  );
}

export default function GeneInfo({ gene }) {
  const [activeTab, setActiveTab] = useState('Overview');

  return (
    <section id="gene" className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b" style={{ background: '#021f30' }}>
        <div className="flex items-center gap-3">
          <div className="px-3 py-1 rounded-lg text-white font-extrabold text-lg tracking-tight border border-white/20"
            style={{ background: 'rgba(249,52,65,0.2)' }}>
            {gene.symbol}
          </div>
          <div>
            <h3 className="text-white font-bold text-base">{gene.full_name}</h3>
            <a
              href={`https://www.ncbi.nlm.nih.gov/gene/?term=${gene.symbol}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 text-xs hover:text-white flex items-center gap-1"
            >
              HGNC: {gene.clingen_gene_id.replace('HGNC:', '')}
              <ExternalLink size={10} />
            </a>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {TAB_LABELS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all ${
              activeTab === tab
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="p-6">
        {activeTab === 'Overview' && <OverviewTab gene={gene} />}
        {activeTab === 'Constraint' && <ConstraintTab gene={gene} />}
        {activeTab === 'Disease' && <DiseaseTab gene={gene} />}
        {activeTab === 'Expression' && <ExpressionTab gene={gene} />}
      </div>
    </section>
  );
}
